import React, { useState, useRef, useEffect, ReactNode } from "react";
import {
  downloadICSFile,
  createGoogleCalendarEventUrl,
} from "../utils/calendarUtils";

interface CalendarEvent {
  title: string;
  description: string;
  startTime: Date;
  endTime: Date;
  addAlarm?: boolean;
  alarmMinutesBefore?: number;
}

interface CalendarSaveButtonProps {
  event: CalendarEvent;
  buttonClass?: string;
  children?: ReactNode;
  className?: string;
  showIcon?: boolean;
}

const CalendarSaveButton: React.FC<CalendarSaveButtonProps> = ({
  event,
  buttonClass,
  children,
  className,
  showIcon = true,
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [addAlarm, setAddAlarm] = useState(false);
  const [alarmMinutesBefore, setAlarmMinutesBefore] = useState(0); // Default 0 minutes
  const optionsRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close the options when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        optionsRef.current &&
        !optionsRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleAlarm = () => {
    setAddAlarm(!addAlarm);
  };

  const handleAlarmTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAlarmMinutesBefore(parseInt(e.target.value, 10));
  };

  const saveToAppleCalendar = () => {
    const eventWithAlarm = {
      ...event,
      addAlarm,
      alarmMinutesBefore,
    };
    downloadICSFile(eventWithAlarm);
    setShowOptions(false);
  };

  const saveToGoogleCalendar = () => {
    const url = createGoogleCalendarEventUrl(
      event.title,
      event.startTime,
      event.endTime,
      event.description,
      addAlarm,
      alarmMinutesBefore,
    );
    window.open(url, "_blank");
    setShowOptions(false);
  };

  return (
    <div className={`calendar-save-container ${className || ""}`}>
      <button
        ref={buttonRef}
        className={`calendar-button ${buttonClass || ""}`}
        onClick={toggleOptions}
        aria-label="Save to calendar"
      >
        {children
          ? children
          : showIcon && (
              <img
                src="/images/apple-calendar-icon.png"
                alt="Calendar"
                width="16"
                height="16"
              />
            )}
      </button>

      {showOptions && (
        <div ref={optionsRef} className="calendar-options">
          <div className="alarm-options">
            <label className="alarm-toggle">
              <input
                type="checkbox"
                checked={addAlarm}
                onChange={() => setAddAlarm(!addAlarm)}
              />
              <span className="alarm-icon">🔔</span> Notificare
            </label>

            {addAlarm && (
              <div className="alarm-time-selector">
                <select
                  value={alarmMinutesBefore}
                  onChange={handleAlarmTimeChange}
                >
                  <option value="0">La moment</option>
                  <option value="5">5 minute înainte</option>
                  <option value="10">10 minute înainte</option>
                  <option value="15">15 minute înainte</option>
                  <option value="30">30 minute înainte</option>
                  <option value="60">1 oră înainte</option>
                </select>
              </div>
            )}
          </div>

          <div className="calendar-providers">
            <button onClick={saveToAppleCalendar} className="apple-option">
              <span className="calendar-option-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 200 245.657"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path d="M167.084 130.514c-.308-31.099 25.364-46.022 26.511-46.761-14.429-21.107-36.91-24.008-44.921-24.335-19.13-1.931-37.323 11.27-47.042 11.27-9.692 0-24.67-10.98-40.532-10.689-20.849.308-40.07 12.126-50.818 30.799-21.661 37.581-5.54 93.281 15.572 123.754 10.313 14.923 22.612 31.688 38.764 31.089 15.549-.612 21.433-10.073 40.242-10.073s24.086 10.073 40.546 9.751c16.737-.308 27.34-15.214 37.585-30.187 11.855-17.318 16.714-34.064 17.009-34.925-.372-.168-32.635-12.525-32.962-49.68l.045-.013zm-30.917-91.287C144.735 28.832 150.524 14.402 148.942 0c-12.344.503-27.313 8.228-36.176 18.609-7.956 9.216-14.906 23.904-13.047 38.011 13.786 1.075 27.862-7.004 36.434-17.376z"></path>
                </svg>
              </span>
              Apple
            </button>
            <button onClick={saveToGoogleCalendar} className="google-option">
              <span className="calendar-option-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 200 245.657"
                  fill="currentColor"
                  width="16"
                  height="16"
                >
                  <path
                    d="M152.637 47.363H47.363v105.273h105.273z"
                    fill="#fff"
                  ></path>
                  <path
                    d="M152.637 200L200 152.637h-47.363z"
                    fill="#f72a25"
                  ></path>
                  <path
                    d="M200 47.363h-47.363v105.273H200z"
                    fill="#fbbc04"
                  ></path>
                  <path
                    d="M152.637 152.637H47.363V200h105.273z"
                    fill="#34a853"
                  ></path>
                  <path
                    d="M0 152.637v31.576A15.788 15.788 0 0 0 15.788 200h31.576v-47.363z"
                    fill="#188038"
                  ></path>
                  <path
                    d="M200 47.363V15.788A15.79 15.79 0 0 0 184.212 0h-31.575v47.363z"
                    fill="#1967d2"
                  ></path>
                  <path
                    d="M15.788 0A15.79 15.79 0 0 0 0 15.788v136.849h47.363V47.363h105.274V0z"
                    fill="#4285f4"
                  ></path>
                  <path
                    d="M68.962 129.02c-3.939-2.653-6.657-6.543-8.138-11.67l9.131-3.76c.83 3.158 2.279 5.599 4.346 7.341 2.051 1.742 4.557 2.588 7.471 2.588 2.995 0 5.55-.911 7.699-2.718 2.148-1.823 3.223-4.134 3.223-6.934 0-2.865-1.139-5.208-3.402-7.031s-5.111-2.718-8.496-2.718h-5.273v-9.033h4.736c2.913 0 5.387-.781 7.389-2.376 2.002-1.579 2.995-3.743 2.995-6.494 0-2.441-.895-4.395-2.686-5.859s-4.053-2.197-6.803-2.197c-2.686 0-4.818.716-6.396 2.148s-2.767 3.255-3.451 5.273l-9.033-3.76c1.204-3.402 3.402-6.396 6.624-8.984s7.34-3.89 12.337-3.89c3.695 0 7.031.716 9.977 2.148s5.257 3.418 6.934 5.941c1.676 2.539 2.507 5.387 2.507 8.545 0 3.223-.781 5.941-2.327 8.187-1.546 2.23-3.467 3.955-5.729 5.143v.537a17.39 17.39 0 0 1 7.34 5.729c1.904 2.572 2.865 5.632 2.865 9.212s-.911 6.771-2.718 9.57c-1.823 2.799-4.329 5.013-7.52 6.624s-6.787 2.425-10.775 2.425c-4.622 0-8.887-1.318-12.826-3.988zm56.087-45.312l-10.026 7.243-5.013-7.601 17.985-12.972h6.901v61.198h-9.847z"
                    fill="#1a73e8"
                  ></path>
                </svg>
              </span>
              Google
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Calendar Icon component for use with the CalendarSaveButton
export const CalendarIcon: React.FC = () => (
  <img
    src="/images/apple-calendar-icon.png"
    alt="Calendar"
    width="16"
    height="16"
  />
);

export default CalendarSaveButton;
